import { authFetch } from '../utils/authFetch';
import { ActiveCustomersResponse, UnpaidInvoicesResponse } from '../types/organizations';

const API_BASE = (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1';

export const customerService = {
  // Get all customers for an organization
  async getAllCustomers(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void,
    page: number = 1,
    pageSize: number = 50,
    category?: string
  ): Promise<ActiveCustomersResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    });
    
    if (category && category !== 'All') {
      params.append('category', category);
    }
    
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/customers?${params.toString()}`, 
      {}, 
      accessToken, 
      setUserAndToken
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch customers');
    }
    
    const result = await response.json();
    // Transform the response to match the expected format
    return {
      status: result.status || 'success',
      organization_id: result.data?.organization_id || organizationId,
      customers: result.data?.customers || [],
      total_count: result.data?.total_count || 0
    };
  },

  // Get all customers for PSE tab (no pagination)
  async getAllCustomersForPSE(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void,
    category?: string
  ): Promise<ActiveCustomersResponse> {
    const params = new URLSearchParams();
    
    if (category && category !== 'All') {
      params.append('category', category);
    }
    
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/customers/all?${params.toString()}`, 
      {}, 
      accessToken, 
      setUserAndToken
    );
    
    const result = await response.json();
    
    return {
      status: result.status || 'success',
      organization_id: result.data?.organization_id || organizationId,
      customers: result.data?.customers || [],
      total_count: result.data?.total_count || 0
    };
  },

  // Get available customer categories for an organization
  async getCustomerCategories(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<{ categories: string[] }> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/customers/categories`, 
      {}, 
      accessToken, 
      setUserAndToken
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch customer categories');
    }
    
    const result = await response.json();
    return {
      categories: result.data?.categories || []
    };
  },

  // Get unpaid invoices for an organization
  async getUnpaidInvoices(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void,
    page: number = 1,
    pageSize: number = 100
  ): Promise<UnpaidInvoicesResponse> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/invoices/unpaid?page=${page}&page_size=${pageSize}`,
      {},
      accessToken,
      setUserAndToken
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch unpaid invoices');
    }
    
    const result = await response.json();
    // Transform the response to match the expected format
    return {
      status: result.status || 'success',
      organization_id: result.data?.organization_id || organizationId,
      invoices: result.data?.invoices || [],
      total_count: result.data?.total_count || 0
    };
  },

  // Get all invoices for an organization
  async getAllInvoices(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void,
    xeroContactId?: string
  ): Promise<UnpaidInvoicesResponse> {
    const url = xeroContactId 
      ? `${API_BASE}/organizations/${organizationId}/invoices?xero_contact_id=${xeroContactId}`
      : `${API_BASE}/organizations/${organizationId}/invoices`;
    
    const response = await authFetch(
      url,
      {},
      accessToken,
      setUserAndToken
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch all invoices');
    }
    
    const result = await response.json();
    // Transform the response to match the expected format
    return {
      status: result.status || 'success',
      organization_id: result.data?.organization_id || organizationId,
      invoices: result.data?.invoices || [],
      total_count: result.data?.total_count || 0
    };
  },
  
  // Get invoices for a specific customer
  async getCustomerInvoices(
    organizationId: string,
    xeroContactId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<UnpaidInvoicesResponse> {
    return this.getAllInvoices(organizationId, accessToken, setUserAndToken, xeroContactId);
  },

  // Get a single customer by ID
  async getCustomerById(
    organizationId: string,
    customerId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<any> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/customers/${customerId}`, 
      {}, 
      accessToken, 
      setUserAndToken
    );
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Customer not found');
      }
      throw new Error('Failed to fetch customer');
    }
    
    const result = await response.json();
    return result.data?.customer;
  },
  async getCustomerCreditTermsLimit(
    organizationId: string,
    xeroDirectContactId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<{ credit_terms?: string | null; credit_terms_desc?: string | null; credit_limit?: number | null; credit_limit_desc?: string | null; status: string; }> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/customers/${xeroDirectContactId}/credit-terms-limit`,
      {},
      accessToken,
      setUserAndToken
    );
    const data = await response.json();
    return data.data;
  },

  async setCustomerCreditTermsLimit(
    organizationId: string,
    xeroDirectContactId: string,
    payload: { credit_terms?: string | null; credit_terms_desc?: string | null; credit_limit?: number | null; credit_limit_desc?: string | null; },
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<void> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/customers/${xeroDirectContactId}/credit-terms-limit`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      },
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to set credit terms/limit');
    }
  },

  async revertCustomerToBaseCredit(
    organizationId: string,
    xeroDirectContactId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<void> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/customers/${xeroDirectContactId}/credit-terms-limit/revert`,
      { method: 'POST' },
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to revert to base credit terms/limit');
    }
  }
  
  // Future methods can be added here:
  // async getCustomerById(...)
  // async getCustomerPayments(...)
  // async getCustomerAnalytics(...)
  // async getCustomerContacts(...)
  // async getCustomerHistory(...)
  // etc.
};
